import { useEffect, useState } from "react";
import {
  Calendar,
  Plus,
  Package,
  ShoppingCart,
  Search,
  Edit,
  Mail,
  FileText,
  Trash2,
} from "lucide-react";
import Tooltip from "@material-ui/core/Tooltip";
import Button from "../../../../components/EcommerceActionButton/Button";
import { Card } from "../../../../components/Card";
import {
  Dialog as AppDialog,
  DialogActions as AppDialogActions,
  DialogContent as AppDialogContent,
  DialogRow as AppDialogRow,
  DialogTitle as AppDialogTitle,
} from "../../../../components/Dialog/Dialog";
import { QuoteForm } from "../../components/QuoteForm";
import EmptyState from "../../../../components/EmptyState/EmptyState";
import { formatNumberToPrice } from "../../../../helpers/priceFormatting";
import { formatDateTime } from "../../../../utils/dateUtils";
import { useSendSalespersonQuoteEmail } from "../../../../utils/react-query/querySalesperson/queries";
import { warningToast } from "../../../../services/toasts";
import styles from "./Quotes.module.scss";

export function Quotes() {
  const storageKey = "salesperson-quotes";
  const [showForm, setShowForm] = useState(false);
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const { mutate: sendQuoteEmail } = useSendSalespersonQuoteEmail();
  const [emailConfirmation, setEmailConfirmation] = useState<Quotation | null>(
    null
  );
  const [deleteConfirmation, setDeleteConfirmation] = useState<number | null>(
    null
  );
  const [pdfLoadingIndex, setPdfLoadingIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return;
      const parsed = JSON.parse(stored) as Quotation[];
      if (Array.isArray(parsed)) {
        setQuotations(parsed);
      }
    } catch (error) {
      localStorage.removeItem(storageKey);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(quotations));
  }, [quotations]);

  const sortedQuotations = quotations
    .map((quotation, originalIndex) => ({ quotation, originalIndex }))
    .sort((a, b) => {
      const firstDate = new Date(a.quotation.date).getTime();
      const secondDate = new Date(b.quotation.date).getTime();

      return secondDate - firstDate;
    });

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const filteredQuotations = sortedQuotations.filter(({ quotation }) => {
    if (!normalizedSearchQuery) return true;

    const searchableText = [
      quotation.clientName,
      quotation.taxId,
      quotation.clientEmail,
      quotation.advisorEmail,
      ...(quotation.products || []).map((product) => product.name),
      ...(quotation.accessories || []).map((accessory) => accessory.name),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return searchableText.includes(normalizedSearchQuery);
  });

  /**
   * Handle saving a new quotation
   */
  const handleSaveQuotation = (quotation: Quotation) => {
    if (editingIndex !== null) {
      // Update existing quotation
      const updatedQuotations = [...quotations];
      updatedQuotations[editingIndex] = quotation;
      setQuotations(updatedQuotations);
      setEditingIndex(null);
    } else {
      // Add new quotation
      setQuotations([...quotations, quotation]);
    }
    setShowForm(false);
  };

  /**
   * Handle editing a quotation
   */
  const handleEditQuotation = (index: number) => {
    setEditingIndex(index);
    setShowForm(true);
  };

  /**
   * Handle deleting a quotation
   */
  const handleDeleteQuotation = (index: number) => {
    setDeleteConfirmation(index);
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmation !== null) {
      setQuotations(quotations.filter((_, i) => i !== deleteConfirmation));
      setDeleteConfirmation(null);
    }
  };

  /**
   * Handle sending email
   */
  const handleSendEmail = (quotation: Quotation) => {
    setEmailConfirmation(quotation);
  };

  const handleConfirmSendEmail = () => {
    if (!emailConfirmation) return;
    const pdfUrl = emailConfirmation.pdfUrl;
    if (!pdfUrl) {
      warningToast("La cotización aún no tiene PDF disponible.");
      setEmailConfirmation(null);
      return;
    }

    sendQuoteEmail(
      {
        pdfUrl,
        clientEmail: emailConfirmation.clientEmail || "",
        clientName: emailConfirmation.clientName || "",
        advisorEmail: emailConfirmation.advisorEmail || "",
      },
      {
        onSettled: () => {
          setEmailConfirmation(null);
        },
      }
    );
  };

  /**
   * Handle PDF export
   */
  const handleExportPDF = (quotation: Quotation, index: number) => {
    if (quotation.pdfUrl) {
      setPdfLoadingIndex(index);
      window.open(quotation.pdfUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => setPdfLoadingIndex(null), 400);
      return;
    }

    warningToast("La cotización aún no tiene PDF disponible.");
  };

  /**
   * Handle opening new quotation form
   */
  const handleNewQuotation = () => {
    setEditingIndex(null);
    setShowForm(true);
  };

  /**
   * Handle closing the form
   */
  const handleCloseForm = () => {
    setShowForm(false);
    setEditingIndex(null);
  };

  // Empty state
  if (!showForm && quotations.length === 0) {
    return (
      <div className={styles.container}>
        <EmptyState
          image={<Calendar size={64} />}
          title="No hay cotizaciones"
          message="Comienza creando tu primera cotización para tus clientes"
        >
          <Button
            onClick={handleNewQuotation}
            color="primary"
            icon={Plus}
            className={styles.compactButton}
          >
            Nueva Cotización
          </Button>
        </EmptyState>
      </div>
    );
  }

  return (
    <>
      <div className={styles.container}>
        {/* Header */}
        {quotations.length > 0 && (
          <>
            <div className={styles.header}>
              <div className={styles.headerInfo}>
                <h2 className={styles.headerTitle}>Cotizaciones</h2>
                <p className={styles.headerSubtitle}>
                  {quotations.length} cotizaciones creadas
                </p>
              </div>
              <Button
                onClick={handleNewQuotation}
                color="primary"
                icon={Plus}
                className={styles.compactButton}
              >
                Nueva Cotización
              </Button>
            </div>

            <div className={styles.searchSection}>
              <div className={styles.searchWrapper}>
                <Search className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Buscar por cliente, NIT, correo o producto..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className={styles.searchInput}
                />
              </div>
              <p className={styles.searchResultText}>
                {filteredQuotations.length} de {quotations.length} cotizaciones
              </p>
            </div>

            {/* Quotations Grid */}
            <div className={styles.quotationsGrid}>
              {filteredQuotations.map(({ quotation, originalIndex }) => (
                <Card
                  key={`${quotation.date}-${originalIndex}`}
                  className={styles.quoteCard}
                >
                  <div className={styles.cardContent}>
                    {/* Header with action buttons */}
                    <div className={styles.cardHeader}>
                      <div className={styles.cardHeaderInfo}>
                        <h3 className={styles.clientName}>
                          {quotation.clientName}
                        </h3>
                        <p className={styles.clientNit}>{quotation.taxId}</p>
                      </div>
                      <div className={styles.cardActions}>
                        <Tooltip title="Editar cotización" arrow>
                          <span>
                            <Button
                              color="outlined"
                              shape="little-rounded"
                              size="small"
                              icon={Edit}
                              onClick={() => handleEditQuotation(originalIndex)}
                              aria-label="Editar cotización"
                              className={styles.iconButton}
                            >
                              {""}
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title="Eliminar cotización" arrow>
                          <span>
                            <Button
                              color="outlined"
                              shape="little-rounded"
                              size="small"
                              icon={Trash2}
                              onClick={() =>
                                handleDeleteQuotation(originalIndex)
                              }
                              aria-label="Eliminar cotización"
                              className={styles.iconButton}
                            >
                              {""}
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title="Enviar por correo" arrow>
                          <span>
                            <Button
                              color="outlined"
                              shape="little-rounded"
                              size="small"
                              icon={Mail}
                              onClick={() => handleSendEmail(quotation)}
                              aria-label="Enviar por correo"
                              className={styles.iconButton}
                            >
                              {""}
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title="Exportar PDF" arrow>
                          <span>
                            <Button
                              color="outlined"
                              shape="little-rounded"
                              size="small"
                              icon={FileText}
                              onClick={() =>
                                handleExportPDF(quotation, originalIndex)
                              }
                              disabled={
                                pdfLoadingIndex !== null || !quotation.pdfUrl
                              }
                              isLoading={pdfLoadingIndex === originalIndex}
                              aria-label="Exportar PDF"
                              className={styles.iconButton}
                            >
                              {""}
                            </Button>
                          </span>
                        </Tooltip>
                      </div>
                    </div>

                    <div className={styles.cardDate}>
                      <Calendar />
                      {formatDateTime(quotation.date)}
                    </div>

                    <div className={styles.cardBadges}>
                      {quotation.products.length > 0 && (
                        <div
                          className={`${styles.badge} ${styles.productsBadge}`}
                        >
                          <Package />
                          {quotation.products.length} productos
                        </div>
                      )}
                      {quotation.accessories.length > 0 && (
                        <div
                          className={`${styles.badge} ${styles.accessoriesBadge}`}
                        >
                          <ShoppingCart />
                          {quotation.accessories.length} accesorios
                        </div>
                      )}
                    </div>

                    <div className={styles.cardTotals}>
                      <div className={styles.totalRow}>
                        <span className={styles.totalLabel}>Subtotal:</span>
                        <span className={styles.totalValue}>
                          {formatNumberToPrice(quotation.subtotal)}
                        </span>
                      </div>
                      <div className={styles.totalRow}>
                        <span className={styles.totalLabel}>IVA 19%:</span>
                        <span className={styles.totalValue}>
                          {formatNumberToPrice(quotation.vat19)}
                        </span>
                      </div>
                      <div
                        className={`${styles.totalRow} ${styles.finalTotal}`}
                      >
                        <span className={styles.totalLabel}>Total:</span>
                        <span className={styles.totalValue}>
                          {formatNumberToPrice(quotation.total)}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>

      <AppDialog
        open={Boolean(emailConfirmation)}
        onOpenChange={(open) => {
          if (!open) {
            setEmailConfirmation(null);
          }
        }}
      >
        <AppDialogTitle>Confirmar envío de correo</AppDialogTitle>
        <AppDialogContent>
          <p className={styles.confirmText}>
            ¿Estás seguro de enviar el correo de esta cotización al cliente?
          </p>
          <AppDialogRow className={styles.confirmSummaryRow}>
            <span>Cliente:</span>
            <strong>{emailConfirmation?.clientName}</strong>
          </AppDialogRow>
          <AppDialogRow className={styles.confirmSummaryRow}>
            <span>Correo:</span>
            <strong>{emailConfirmation?.clientEmail || "Sin correo"}</strong>
          </AppDialogRow>
          <AppDialogActions>
            <Button
              color="outlined"
              shape="little-rounded"
              onClick={() => setEmailConfirmation(null)}
              className={styles.compactButton}
            >
              Cancelar
            </Button>
            <Button
              color="primary"
              shape="little-rounded"
              onClick={handleConfirmSendEmail}
              className={styles.compactButton}
            >
              Confirmar envío
            </Button>
          </AppDialogActions>
        </AppDialogContent>
      </AppDialog>

      <AppDialog
        open={deleteConfirmation !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteConfirmation(null);
          }
        }}
      >
        <AppDialogTitle>Confirmar eliminación</AppDialogTitle>
        <AppDialogContent>
          <p className={styles.confirmText}>
            ¿Estás seguro de que deseas eliminar esta cotización? Esta acción no
            se puede deshacer.
          </p>
          {deleteConfirmation !== null && quotations[deleteConfirmation] && (
            <AppDialogRow className={styles.confirmSummaryRow}>
              <span>Cliente:</span>
              <strong>{quotations[deleteConfirmation]?.clientName}</strong>
            </AppDialogRow>
          )}
          <AppDialogActions>
            <Button
              color="outlined"
              shape="little-rounded"
              onClick={() => setDeleteConfirmation(null)}
              className={styles.compactButton}
            >
              Cancelar
            </Button>
            <Button
              color="primary"
              shape="little-rounded"
              onClick={handleConfirmDelete}
              className={styles.compactButton}
            >
              Eliminar
            </Button>
          </AppDialogActions>
        </AppDialogContent>
      </AppDialog>

      {/* Quote Form */}
      <QuoteForm
        isOpen={showForm}
        onClose={handleCloseForm}
        onSave={handleSaveQuotation}
        editingQuotation={
          editingIndex !== null ? quotations[editingIndex] : undefined
        }
      />
    </>
  );
}
